import { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material'
import { format, money, type CurrencyCode } from '@iobiz/shared'

/**
 * Apport ou prélèvement d'espèces, en cours de session.
 *
 * ⭐ Le MOTIF est obligatoire, et c'est tout l'intérêt de l'écran. Un
 * mouvement sans raison ne vaut pas mieux qu'un écart inexpliqué : il déplace
 * simplement la question du comptage vers le journal. « Prélèvement 200 000 »
 * n'apprend rien ; « dépôt banque » clôt le sujet.
 *
 * ⭐ Des motifs PROPOSÉS, pas imposés : ce sont les trois quarts des cas, et
 * les taper vingt fois par semaine use. La liste reste ouverte — les usages
 * d'un salon ne sont pas ceux d'un négociant.
 */

const MOTIFS: Record<'apport' | 'prelevement', readonly string[]> = {
  apport: ['Appoint de monnaie', 'Complément de fond', 'Remboursement reçu'],
  prelevement: ['Dépôt en banque', 'Achat fournisseur', 'Avance sur salaire', 'Retrait gérant'],
}

export function MouvementDialog({
  open,
  sens,
  devise,
  onClose,
  onValider,
}: {
  open: boolean
  sens: 'apport' | 'prelevement'
  devise: CurrencyCode
  onClose: () => void
  /** Montant SIGNÉ : le sens est porté par le signe, jamais par un drapeau. */
  onValider: (montant: number, motif: string) => Promise<void> | void
}): JSX.Element {
  const [saisie, setSaisie] = useState('')
  const [motif, setMotif] = useState('')
  const [enCours, setEnCours] = useState(false)

  useEffect(() => {
    if (open) {
      setSaisie('')
      setMotif('')
    }
  }, [open, sens])

  const montant = Number(saisie.replace(',', '.')) || 0
  const valide = montant > 0 && motif.trim().length >= 2
  const apport = sens === 'apport'

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{apport ? 'Apport en caisse' : 'Prélèvement en caisse'}</DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 4, pt: 4 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {apport
            ? 'Espèces ajoutées au tiroir sans qu’il s’agisse d’une vente.'
            : 'Espèces sorties du tiroir sans qu’il s’agisse d’un remboursement.'}
        </Typography>

        <TextField
          autoFocus
          label="Montant"
          value={saisie}
          onChange={(e) => setSaisie(e.target.value.replace(/[^\d.,]/g, ''))}
          inputProps={{ inputMode: 'decimal' }}
          fullWidth
        />

        <Box>
          <TextField
            label="Motif"
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
            placeholder={apport ? 'Appoint de monnaie' : 'Dépôt en banque'}
            fullWidth
          />
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
            {MOTIFS[sens].map((m) => (
              <Chip key={m} label={m} size="small" variant="outlined" onClick={() => setMotif(m)} />
            ))}
          </Box>
        </Box>

        {/* ⚠️ On dit AVANT de valider que le mouvement ne se reprend pas. La
            table est en ajout seul : corriger se fait par un mouvement
            inverse, ce qui laisse deux lignes et leur raison. */}
        <Alert severity="info">
          Un mouvement enregistré ne se modifie pas : il se corrige par un mouvement inverse.
        </Alert>

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Effet sur la caisse
          </Typography>
          <Typography className="tabular" sx={{ fontWeight: 700 }}>
            {apport ? '+' : '−'} {format(money(montant, devise), 'fr-FR')}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 6, py: 4 }}>
        <Button onClick={onClose}>Annuler</Button>
        <Button
          variant="contained"
          disabled={!valide || enCours}
          onClick={async () => {
            setEnCours(true)
            try {
              await onValider(Math.round(apport ? montant : -montant), motif.trim())
            } finally {
              setEnCours(false)
            }
          }}
        >
          {enCours ? 'Enregistrement…' : 'Enregistrer'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
